import {
  Activity,
  Wifi,
  Database,
  HardDrive,
  RefreshCw,
  Heart,
  Shield,
  Waves,
} from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useTheme } from "../context/ThemeContext";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";
import { Spin } from "antd";
import InfoTooltip from "./ui/InfoTooltip";

export function SystemHealth({ onClick, data, loading }) {
  const { systemHealth, systemData, systemDevice, last, next, healthDetails } =
    data || {};
  const { theme, colors } = useTheme();

  // const healthDetails = data?.data?.data || data?.data || {};

  const allMeterCount = Number(healthDetails?.all_meter_count || 0);

  const getSafeCount = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  };

  const getWithoutCount = (errorCount) =>
    Math.max(allMeterCount - getSafeCount(errorCount), 0);

  useEffect(() => {
    console.log("System Health Data:", data);
  }, [data]);

  const healthValue = Number(systemHealth) || 0;

  let healthStatus = "Healthy";
  let healthColor = "#2b9f7a"; // green
  let healthGradientFrom = "#2b9f7a";
  let healthGradientTo = "#228385";

  if (healthValue <= 60) {
    healthStatus = "UnHealthy";
    healthColor = "#ef4444"; // red
    healthGradientFrom = "#ef4444";
    healthGradientTo = "#b91c1c";
  } else if (healthValue > 60 && healthValue <= 80) {
    healthStatus = "Alarming";
    healthColor = "#f97316"; // orange
    healthGradientFrom = "#f97316";
    healthGradientTo = "#ea580c";
  }

  const metrics = [
    {
      label: "Communication Status",
      icon: Heart,
      value: `${Math.min(systemHealth ?? 0, 100).toFixed(0)}%`,
      status: systemHealth > 90 ? "Excellent" : "Warning",
      color:
        systemHealth >= 80
          ? "#32cd32"
          : systemHealth >= 60
            ? "#ff8c00"
            : "#d13838",
      tab: "communicationStatus",
      tooltipPosition: "right",
      info: "Shows the overall communication status of meters based on last communication time and encoder communication errors.",
      hoverDetails: [
        {
          label: "Meters With Communication Errors",
          value: getSafeCount(healthDetails?.communication_data_count),
        },
        {
          label: "Meters Without Communication Errors",
          value: getWithoutCount(healthDetails?.communication_data_count),
        },
      ],
    },
    {
      label: "Data Status",
      icon: Database,
      value: `${Math.min(systemData ?? 0, 100).toFixed(0)}%`,
      status: systemData > 95 ? "Active" : "Warning",
      color:
        systemData >= 80 ? "#32cd32" : systemData >= 60 ? "#ff8c00" : "#d13838",
      tab: "dataStatus",
      info: "Determined by the number of outliers detected in meter flow data over the last two weeks.",
      hoverDetails: [
        {
          label: "Meters With Outliers",
          value: getSafeCount(healthDetails?.outlier_count),
        },
        {
          label: "Meters Without Outliers",
          value: getWithoutCount(healthDetails?.outlier_count),
        },
      ],
    },
    {
      label: "Device Status",
      icon: HardDrive,
      value: `${Math.min(systemDevice ?? 0, 100).toFixed(0)}%`,
      status: systemDevice > 90 ? "Healthy" : "Warning",
      color:
        systemDevice >= 80
          ? "#32cd32"
          : systemDevice >= 60
            ? "#ff8c00"
            : "#d13838",
      tab: "deviceStatus",
      info: "Shows the overall device status of meters based on endpoint tamper events, battery level, and signal strength.",
      hoverDetails: [
        {
          label: "Meters With Device Errors",
          value: getSafeCount(healthDetails?.device_data_count),
        },
        {
          label: "Meters Without Device Errors",
          value: getWithoutCount(healthDetails?.device_data_count),
        },
      ],
    },
  ];

  const formatDateTime = (value) => {
    if (!value) {
      return {
        date: "-",
        time: "-",
      };
    }

    const parts = value.split(" ");

    return {
      date: `${parts[0]} ${parts[1]} ${parts[2]}`,
      time: `${parts[3]} ${parts[4]}`,
    };
  };

  const lastDateTime = formatDateTime(last);
  const nextDateTime = formatDateTime(next);

  const getTooltipClasses = (position) => {
    if (position === "right") {
      return {
        popup:
          "absolute left-1/2 bottom-full z-[9999] mb-3 w-60 -translate-x-1/2 rounded-xl border p-3 shadow-xl opacity-0 invisible group-hover/metric:opacity-100 group-hover/metric:visible transition-all duration-200 pointer-events-none",
        arrow:
          "absolute -bottom-2 left-1/2 h-4 w-4 -translate-x-1/2 rotate-45 border-r border-b",
      };
    }

    if (position === "left") {
      return {
        popup:
          "absolute right-full top-1/2 z-[9999] mr-3 w-60 -translate-y-1/2 rounded-xl border p-3 shadow-xl opacity-0 invisible group-hover/metric:opacity-100 group-hover/metric:visible transition-all duration-200 pointer-events-none",
        arrow:
          "absolute -right-2 top-1/2 h-4 w-4 -translate-y-1/2 rotate-45 border-r border-t",
      };
    }

    return {
      popup:
        "absolute left-1/2 top-full z-[9999] mt-3 w-60 -translate-x-1/2 rounded-xl border p-3 shadow-xl opacity-0 invisible group-hover/metric:opacity-100 group-hover/metric:visible transition-all duration-200 pointer-events-none",
      arrow:
        "absolute -top-2 left-1/2 h-4 w-4 -translate-x-1/2 rotate-45 border-l border-t",
    };
  };

  return (
    <motion.div
      className="rounded-xl p-6 border-l-[0.5px] relative overflow-visible h-full flex flex-col group transition-colors duration-300 z-20"
      style={{
        backgroundColor: theme === "white" ? "#ffffff" : colors.glassEffect,
        // backgroundColor: colors.cardBg,
        backdropFilter: "blur(10px)",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: theme === "white" ? colors.border : `${colors.primary}15`,
      }}
      initial={{ opacity: 0, x: -50, rotateZ: -10 }}
      animate={{ opacity: 1, x: 0, rotateZ: 0 }}
      transition={{ duration: 0.8, delay: 0.1, type: "spring", bounce: 0.4 }}
      whileHover={{
        x: 10,
        transition: { type: "spring", stiffness: 300 },
      }}
      onClick={onClick}
    >
      {/* Animated top line on hover */}
      <div
        className="absolute top-0 left-0 h-0.5 w-0 group-hover:w-full transition-all duration-400 ease-out rounded-full"
        style={{
          backgroundImage: colors.gradient,
        }}
      />

      <motion.div
        className="absolute top-0 right-0 w-72 h-72 rounded-full blur-3xl"
        style={{
          background: `radial-gradient(circle, ${colors.primary}20, transparent 70%)`,
        }}
      />

      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-4">
          <div
            className="p-2 rounded-lg"
            style={{ backgroundImage: colors.gradient }}
          >
            <Waves className="w-5 h-5 text-white" />
          </div>
          <h3
            className="text-2xl font-bold tracking-tight"
            style={{ color: colors.text }}
          >
            System Health
          </h3>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Spin size="large" />
          </div>
        ) : (
          <>
            {/* Pulsing Heart */}
            <div className="flex items-center justify-center mb-4">
              <motion.div
                className="relative"
                animate={{ scale: [1, 1.15, 1] }}
                transition={{
                  duration: 1.2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <svg
                  width="180"
                  height="180"
                  viewBox="0 0 24 24"
                  className="w-44 h-44"
                >
                  <defs>
                    <linearGradient
                      id="heartGradientTop"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor={healthGradientFrom} />
                      <stop offset="100%" stopColor={healthGradientTo} />
                    </linearGradient>

                    <linearGradient
                      id="heartGradientMiddle"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor={healthGradientFrom} />
                      <stop offset="100%" stopColor={healthGradientTo} />
                    </linearGradient>

                    <linearGradient
                      id="heartGradientBottom"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor={healthGradientFrom} />
                      <stop offset="100%" stopColor={healthGradientTo} />
                    </linearGradient>

                    <filter
                      id="heartShadow"
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="200%"
                    >
                      <feGaussianBlur in="SourceAlpha" stdDeviation="0.4" />
                      <feOffset dx="0" dy="0.5" result="offsetblur" />
                      <feComponentTransfer>
                        <feFuncA type="linear" slope="0.5" />
                      </feComponentTransfer>
                      <feMerge>
                        <feMergeNode />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>

                  {/* Bottom */}
                  <motion.path
                    d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                    fill="url(#heartGradientBottom)"
                    transform="translate(0.6, 0.6)"
                    opacity="0.5"
                    animate={{ scale: [1, 1.02, 1] }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    style={{ transformOrigin: "center" }}
                  />

                  {/* Middle */}
                  <motion.path
                    d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                    fill="url(#heartGradientMiddle)"
                    transform="translate(0.3, 0.3)"
                    filter="url(#heartShadow)"
                    animate={{ scale: [1, 1.03, 1] }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    style={{ transformOrigin: "center" }}
                  />

                  {/* Top */}
                  <motion.path
                    d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                    fill="url(#heartGradientTop)"
                    stroke={healthGradientFrom}
                    strokeWidth="0.3"
                    filter="url(#heartShadow)"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    style={{ transformOrigin: "center" }}
                  />
                </svg>
              </motion.div>
            </div>
            <motion.div
              className="flex items-center justify-center gap-2 mb-6"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <motion.div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: healthColor }}
                animate={{
                  scale: [1, 1.3, 1],
                  boxShadow: [
                    `0 0 0px ${healthColor}80`,
                    `0 0 10px ${healthColor}`,
                    `0 0 0px ${healthColor}80`,
                  ],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              <span
                className="text-transparent bg-clip-text text-sm font-bold tracking-wide"
                style={{
                  backgroundImage: `linear-gradient(to right, ${healthGradientFrom}, ${healthGradientTo})`,
                  WebkitTextFillColor: "transparent",
                  WebkitBackgroundClip: "text",
                }}
              >
                Status: {healthStatus}
              </span>
            </motion.div>

            {/* Metrics */}
            <div className="grid grid-cols-3 max-[1650px]:grid-cols-2 gap-4 mb-6 relative z-50">
              {metrics.map((metric, index) => {
                const Icon = metric.icon;
                const tooltipClasses = getTooltipClasses(
                  metric.tooltipPosition,
                );

                return (
                  <motion.div
                    key={index}
                    className="relative rounded-lg p-4 border transition-colors cursor-pointer h-full group/metric"
                    style={{
                      backgroundColor: colors.background,
                      borderColor: colors.border,
                      height: "100%",
                    }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1 + index * 0.1 }}
                    whileHover={{ y: -3 }}
                    onClick={(e) => {
                      if (e.target.closest("button")) return;
                      if (e.target.closest(".fixed")) return;

                      e.stopPropagation();
                      onClick?.(metric.tab);
                    }}
                  >
                    <div className="relative flex flex-col items-center text-center">
                      <p
                        className="text-2xl font-bold mb-2"
                        style={{ color: metric.color }}
                      >
                        {metric.value}
                      </p>

                      <div className="flex items-center justify-center relative">
                        <p
                          className="text-xs font-medium"
                          style={{ color: colors.textSecondary }}
                        >
                          {metric.label}
                        </p>

                        {metric.info && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <InfoTooltip text={metric.info} />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Hover Popup */}
                    <div
                      className={tooltipClasses.popup}
                      style={{
                        backgroundColor:
                          theme === "white" ? "#ffffff" : colors.background,
                        borderColor: colors.border,
                        color: colors.text,
                      }}
                    >
                      <div
                        className={tooltipClasses.arrow}
                        style={{
                          backgroundColor:
                            theme === "white" ? "#ffffff" : colors.background,
                          borderColor: colors.border,
                        }}
                      />

                      <p
                        className="mb-2 text-xs font-bold"
                        style={{ color: colors.text }}
                      >
                        {metric.label} Details
                      </p>

                      <div className="space-y-2">
                        {metric.hoverDetails?.map((item, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between gap-3 rounded-lg px-3 py-2"
                            style={{
                              backgroundColor: `${colors.primary}10`,
                            }}
                          >
                            <span
                              className="text-xs font-medium"
                              style={{ color: colors.textSecondary }}
                            >
                              {item.label}
                            </span>

                            <span
                              className="text-sm font-bold"
                              style={{ color: metric.color }}
                            >
                              {item.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
            <div
              className="py-6 border-t"
              style={{ borderColor: colors.border }}
            >
              {/* Two cards below */}
              <div className="grid grid-cols-2 gap-3">
                <motion.div
                  className="backdrop-blur-sm rounded-lg p-3 border transition-colors"
                  style={{
                    backgroundColor: `${colors.primary}10`,
                    borderColor: `${colors.primary}20`,
                  }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <span
                    className="text-xs block mb-1"
                    style={{ color: colors.textSecondary }}
                  >
                    Last Updated
                  </span>
                  {/* Last Updated */}
                  <span
                    className="text-sm font-medium block"
                    style={{ color: colors.text }}
                  >
                    {lastDateTime.date}
                  </span>

                  <span
                    className="text-xs"
                    style={{ color: colors.textSecondary }}
                  >
                    {lastDateTime.time}
                  </span>
                </motion.div>

                <motion.div
                  className="backdrop-blur-sm rounded-lg p-3 border transition-colors"
                  style={{
                    backgroundColor: `${colors.primary}10`,
                    borderColor: `${colors.primary}20`,
                  }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <span
                    className="text-xs block mb-1"
                    style={{ color: colors.textSecondary }}
                  >
                    Next Update
                  </span>
                  {/* Next Update */}
                  <span
                    className="text-sm font-medium block"
                    style={{ color: colors.text }}
                  >
                    {nextDateTime.date}
                  </span>

                  <span
                    className="text-xs"
                    style={{ color: colors.textSecondary }}
                  >
                    {nextDateTime.time}
                  </span>
                </motion.div>
              </div>
            </div>
               {/* <div
              className="pt-4 border-t flex justify-between text-sm"
              style={{ borderColor: colors.border }}
            >
              <span
                className="font-semibold"
                style={{ color: colors.success || colors.primary }}
              >
                Total Meters: {allMeterCount}
              </span>
            </div> */}
          </>
        )}
      </div>
    </motion.div>
  );
}
